import {useEffect, useState} from 'react';

import BadgeCard from '@/components/BadgeCard/BadgeCard';
import BadgeDetailDrawer from '@/components/BadgeDetailDrawer/BadgeDetailDrawer';
import type {PluralFormsModel} from '@/types/content/pluralForms';
import type {Badge} from '@/types/loyalty';
import {twx} from '@/utils/tailwindUtils';

type BadgesGridProps = {
    badges: Badge[];
    className?: string;
    showProgress?: boolean;
    actionsRemainingPluralForms?: PluralFormsModel;
};

const BadgesGrid = ({badges, className, showProgress = false, actionsRemainingPluralForms}: BadgesGridProps) => {
    const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);

    // Keep the selected badge in sync with the latest data. If it disappears
    // from the data set (e.g. after a refetch following an external link),
    // close the detail drawer. If it was updated, propagate the fresh data.
    useEffect(() => {
        if (!selectedBadge) return;
        const updated = badges.find((b) => b.id === selectedBadge.id);
        if (!updated) {
            setSelectedBadge(null);
        } else if (updated !== selectedBadge) {
            setSelectedBadge(updated);
        }
    }, [badges, selectedBadge]);

    if (!badges.length) return null;

    return (
        <>
            <div className={twx('grid grid-cols-2 gap-2', className)}>
                {badges.map((badge) => (
                    <BadgeCard
                        key={badge.id}
                        badge={badge}
                        showProgress={showProgress}
                        actionsRemainingPluralForms={actionsRemainingPluralForms}
                        onClick={badge.isSingleLevel ? () => setSelectedBadge(badge) : undefined}
                    />
                ))}
            </div>

            <BadgeDetailDrawer
                badge={selectedBadge}
                open={selectedBadge !== null}
                onClose={() => setSelectedBadge(null)}
            />
        </>
    );
};

export default BadgesGrid;
